import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from './store/hooks';
import { selectSettingsLoading, selectSettings, selectSettingsError } from './store/selectors';
import { useSession } from './hooks/useSession';

import { LocalizationProvider } from './context/LocalizationContext';
import Notification from './components/Notification';
import AppRoutes from './routes';
import './App.css';

const App: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const rootPath = process.env.REACT_APP_ROOT_PATH || '';

  const settingsLoading = useAppSelector(selectSettingsLoading);
  const settings = useAppSelector(selectSettings);
  const settingsError = useAppSelector(selectSettingsError);
  const { sessionId, loading: sessionLoading, experimentsLoaded } = useSession();

  const [isAppVisible, setIsAppVisible] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  const isDefaultRoute = location.pathname === `${rootPath}/` || location.pathname === '/';
  const isGameRoute = location.pathname === `${rootPath}/game` || location.pathname === '/game';

  const navigateTo = (path: string) => {
    navigate(`${rootPath}${path}`);
  };

  useEffect(() => {
    if (isDefaultRoute) {
      if (!hasInitialized) {
        setHasInitialized(true);
        setTimeout(() => setIsAppVisible(true), 50);
      }
      return;
    }

    // For Game route, initialize immediately after session is ready (don't wait for settings)
    if (isGameRoute) {
      const isSessionReady = !sessionLoading && experimentsLoaded;

      if (isSessionReady && !hasInitialized) {
        setHasInitialized(true);
        setTimeout(() => setIsAppVisible(true), 50);
      }
      return;
    }

    // For other routes, wait for both session and settings
    const isSessionReady = !sessionLoading && experimentsLoaded;
    const areSettingsReady = !settingsLoading && (settings !== null || settingsError !== null);

    if (isSessionReady && areSettingsReady && !hasInitialized) {
      setHasInitialized(true);
      setTimeout(() => setIsAppVisible(true), 50);
    }
  }, [
    isDefaultRoute,
    isGameRoute,
    sessionLoading,
    experimentsLoaded,
    settingsLoading,
    settings,
    settingsError,
    hasInitialized,
    location.pathname,
  ]);

  useEffect(() => {
    if (!isDefaultRoute && !isGameRoute && !hasInitialized) {
      const fallbackTimer = setTimeout(() => {
        console.warn('App initialization timeout - showing app with fallback state');
        setHasInitialized(true);
        setIsAppVisible(true);
      }, 10000);

      return () => clearTimeout(fallbackTimer);
    }
  }, [isDefaultRoute, isGameRoute, hasInitialized]);

  return (
    <LocalizationProvider>
      <div className={`app-container ${isAppVisible ? 'app-visible' : 'app-hidden'}`}>
        <AppRoutes />
        <Notification />
      </div>
    </LocalizationProvider>
  );
};

export default App;
